import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TipoMoneda } from '../../types/cobros';
import { useCajasContabilidad } from '../../hooks/useCajasContabilidad';

interface ModalConfirmarCobroTotalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cajaId?: number | null) => Promise<any>;
  moneda: TipoMoneda;
  saldoPendiente: number;
  isLoading?: boolean;
}

const formatearMonto = (monto: number, moneda: TipoMoneda): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
  }).format(monto);
};

export const ModalConfirmarCobroTotal: React.FC<ModalConfirmarCobroTotalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  moneda,
  saldoPendiente,
  isLoading = false,
}) => {
  const [cajaId, setCajaId] = useState<number | null>(null);

  const { cajas } = useCajasContabilidad();
  const cajasFiltradas = cajas.filter((c) => c.moneda === moneda);

  const handleConfirm = async () => {
    try {
      await onConfirm(cajaId);
      handleClose();
    } catch (err) {
      // El error se maneja en el nivel superior
      console.error('Error al registrar cobro total:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setCajaId(null);
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmar Cobro Total</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            ¿Confirmar cobro total por{' '}
            <strong className="text-foreground">{formatearMonto(saldoPendiente, moneda)}</strong>?
          </p>
          <p className="text-sm text-muted-foreground">
            Esto registrará automáticamente el saldo restante como un cobro.
          </p>

          {cajasFiltradas.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="caja-cobro-total">
                Caja destino <span className="text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Select
                value={cajaId ? String(cajaId) : ''}
                onValueChange={(value) => setCajaId(value ? Number(value) : null)}
                disabled={isLoading}
              >
                <SelectTrigger id="caja-cobro-total" className="w-full">
                  <SelectValue placeholder="Seleccionar caja destino" />
                </SelectTrigger>
                <SelectContent>
                  {cajasFiltradas.map((caja) => (
                    <SelectItem key={caja.id} value={String(caja.id)}>
                      {caja.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="secondary"
            onClick={handleConfirm}
            loading={isLoading}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
